import { Button, Icon, cn } from "@sagot/ui";
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { ErrorPayload } from "../types";

/** Distance from the bottom (px) still treated as "following" the conversation. */
const PIN_THRESHOLD_PX = 48;

export interface ChatLogProps {
  children: ReactNode;
  /** True while an answer streams: the log is aria-busy so screen readers read the finished answer once. */
  busy?: boolean;
  label?: string;
  jumpLabel?: string;
  className?: string;
}

/**
 * `role="log"` + `aria-live="polite"` message list. Auto-scrolls on new content only while the
 * user is at (or near) the bottom; scrolling up pauses it and shows "Jump to latest".
 */
export function ChatLog({ children, busy = false, label = "Conversation", jumpLabel = "Jump to latest", className }: ChatLogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const [showJump, setShowJump] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  });

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_THRESHOLD_PX;
    pinned.current = atBottom;
    setShowJump(!atBottom);
  };

  const jump = () => {
    const el = ref.current;
    if (!el) return;
    pinned.current = true;
    setShowJump(false);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight, behavior: reduce ? "auto" : "smooth" });
    else el.scrollTop = el.scrollHeight;
  };

  return (
    <div className={cn("relative min-h-0 flex-1", className)}>
      <div
        ref={ref}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label={label}
        aria-busy={busy || undefined}
        // Scrollable region must be keyboard-scrollable (axe: scrollable-region-focusable).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- see above
        tabIndex={0}
        onScroll={onScroll}
        className="h-full space-y-4 overflow-y-auto outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {children}
      </div>
      {showJump ? (
        <Button variant="secondary" size="sm" onClick={jump} className="absolute bottom-3 left-1/2 -translate-x-1/2 shadow-pop">
          <Icon name="chevron-down" />
          {jumpLabel}
        </Button>
      ) : null}
    </div>
  );
}

export interface ErrorNoticeProps {
  error: ErrorPayload | null;
  onRetry?: () => void;
  labels?: Partial<Record<"generic" | "network" | "rateLimited" | "unsupported" | "retry", string>> & { rateLimitedIn?: (s: number) => string };
}

/** Friendly copy per error code + retry. Never shows raw server/provider messages. */
export function errorCopy(error: ErrorPayload, labels: ErrorNoticeProps["labels"] = {}): string {
  if (error.code === "rate_limited") {
    if (error.retry_after !== undefined && labels.rateLimitedIn) return labels.rateLimitedIn(error.retry_after);
    if (error.retry_after !== undefined) return `You're sending messages quickly. Please try again in ${Math.ceil(error.retry_after)} s.`;
    return labels.rateLimited ?? "You're sending messages quickly. Please wait a moment and try again.";
  }
  if (error.code === "network") return labels.network ?? "Connection lost. Check your internet and try again.";
  if (error.code === "stream_unsupported") return labels.unsupported ?? "This browser couldn't receive the answer. Please try again.";
  return labels.generic ?? "Something went wrong on our side. Please try again.";
}

export function ErrorNotice({ error, onRetry, labels = {} }: ErrorNoticeProps) {
  if (!error) return null;
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-danger-soft px-3 py-2 text-xs text-fg">
      <Icon name="alert-circle" className="size-4 text-danger" />
      <span className="min-w-0 flex-1">{errorCopy(error, labels)}</span>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <Icon name="refresh-cw" />
          {labels.retry ?? "Try again"}
        </Button>
      ) : null}
    </div>
  );
}

/** Under an unsent user bubble: "Not sent · Tap to retry". */
export function UnsentNotice({ onRetry, label = "Not sent · Tap to retry" }: { onRetry: () => void; label?: string }) {
  return (
    <button type="button" onClick={onRetry} className="mt-1 inline-flex min-h-6 cursor-pointer items-center gap-1 text-[11px] font-semibold text-danger hover:underline">
      <Icon name="alert-circle" className="size-3.5" />
      {label}
    </button>
  );
}

const CHIP_BUTTON = "group inline-flex max-w-full cursor-pointer items-center bg-transparent p-0 text-left pointer-coarse:min-h-11 disabled:cursor-not-allowed disabled:opacity-50";
const CHIP_FACE =
  "min-w-0 break-words rounded-[min(var(--wg-radius,12px),12px)] border border-line px-2.5 py-1 text-xs font-medium leading-snug text-primary-ink group-enabled:group-hover:border-primary group-enabled:group-hover:bg-primary-soft";

export interface FollowUpsProps {
  items: readonly string[];
  onAsk: (text: string) => void;
  disabled?: boolean;
  /** Accessible list name when there is no visible `heading`. */
  label?: string;
  /** Small visible label above the chips ("Related questions"); also names the list. */
  heading?: string;
  /** Show at most this many chips. */
  max?: number;
}

/**
 * Suggested-question chips (starters under the greeting, follow-ups under an answer). Compact and
 * light: each chip shrinks to its text and wraps inside the column, and on touch devices its
 * button is a transparent 44 px target around the small visual chip (CLAUDE.md rule 8).
 */
export function FollowUps({ items, onAsk, disabled, label = "Suggested questions", heading, max }: FollowUpsProps) {
  const headingId = useId();
  const shown = max === undefined ? items : items.slice(0, max);
  if (shown.length === 0) return null;
  return (
    <div className="mt-3">
      {heading ? (
        <p id={headingId} className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-fg-subtle">
          {heading}
        </p>
      ) : null}
      <ul aria-label={heading ? undefined : label} aria-labelledby={heading ? headingId : undefined} className="flex flex-wrap items-start gap-x-1.5 gap-y-1.5 pointer-coarse:gap-y-0">
        {shown.map((item) => (
          <li key={item} className="max-w-full">
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                onAsk(item);
              }}
              className={CHIP_BUTTON}
            >
              <span className={CHIP_FACE}>{item}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
